import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarCheck, CalendarClock, MapPin } from 'lucide-react';
import { useLocale, useT } from '../lib/i18n.js';
import { DomainApiError } from '../lib/api-error.js';
import {
  bookPublicViewing,
  cancelPublicViewing,
  getPublicViewing,
  reschedulePublicViewing,
  type PublicViewing,
} from '../lib/viewings-api.js';
import { formatViewingDay, formatViewingRange, formatViewingTime, groupSlotsByDay, officeAccentStyle } from '../lib/viewing-format.js';
import { V4Group, V4Header, V4NextStep, V4Row } from '../components/ux-v4.js';
import { PropertyChat } from '../components/property-chat.js';

/**
 * `/prohlidka/<token>` — veřejná stránka, kde si zájemce vybere termín
 * prohlídky (krok CRM4, bod 1). Bez přihlášení; autorizuje token z odkazu.
 *
 * Vzor BOKOLO (`docs/agent-briefs/cloud/ref-crm/modules/booking-engine`):
 * výběr VŽDY INLINE na stránce (žádný iframe), jeden krok = jedna věc —
 * vyber termín → vyplň jméno a e-mail → hotovo. Termín, který mezitím zabral
 * někdo jiný, vrátí 409 a stránka nabídku obnoví („vyberte jiný"), ne chybovou
 * obrazovku.
 *
 * Zrušení a přesun jdou TOUTÉŽ adresou (odkaz v potvrzovacím e-mailu).
 * Stránka žádná osobní data neukládá do prohlížeče.
 */
export function ProhlidkaPage({ token }: { readonly token: string }): React.JSX.Element {
  const t = useT();
  const locale = useLocale();
  const queryClient = useQueryClient();
  const queryKey = ['public-viewing', token];
  const viewing = useQuery({ queryKey, queryFn: () => getPublicViewing(token), retry: false });
  const [chosen, setChosen] = React.useState<string | null>(null);
  const [moving, setMoving] = React.useState(false);
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [smsConsent, setSmsConsent] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [cancelled, setCancelled] = React.useState(false);

  React.useEffect(() => {
    if (viewing.data === undefined) return;
    setName((current) => (current === '' ? (viewing.data.contactName ?? '') : current));
    setEmail((current) => (current === '' ? (viewing.data.contactEmail ?? '') : current));
  }, [viewing.data]);

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey });
  }

  function explain(error: unknown): string {
    if (error instanceof DomainApiError) {
      if (error.status === 409) return t('webAdmin.publicViewing.slotTaken');
      if (error.status === 410) return t('webAdmin.publicViewing.expired');
      if (error.status === 404) return t('webAdmin.publicViewing.notFound');
    }
    return t('webAdmin.publicViewing.failed');
  }

  async function submit(): Promise<void> {
    if (chosen === null) return;
    setBusy(true);
    setNotice(null);
    try {
      if (moving) {
        await reschedulePublicViewing(token, chosen);
      } else {
        await bookPublicViewing(token, {
          slotId: chosen,
          contactName: name.trim(),
          contactEmail: email.trim(),
          ...(phone.trim() !== '' ? { contactPhone: phone.trim() } : {}),
          ...(phone.trim() !== '' ? { smsConsent } : {}),
        });
      }
      setChosen(null);
      setMoving(false);
      setCancelled(false);
      await refresh();
    } catch (error) {
      setNotice(explain(error));
      setChosen(null);
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  async function cancel(): Promise<void> {
    setBusy(true);
    setNotice(null);
    try {
      await cancelPublicViewing(token);
      setCancelled(true);
      await refresh();
    } catch (error) {
      setNotice(explain(error));
    } finally {
      setBusy(false);
    }
  }

  if (viewing.isLoading) {
    return (
      <main className="rh-v4-page rh-public-viewing" data-testid="public-viewing">
        <p role="status" className="rh-a2-status">
          {t('webAdmin.publicViewing.loading')}
        </p>
      </main>
    );
  }
  if (viewing.error !== null || viewing.data === undefined) {
    return (
      <main className="rh-v4-page rh-public-viewing" data-testid="public-viewing">
        <V4Header title={t('webAdmin.publicViewing.title')} />
        <V4NextStep
          testId="public-viewing-unavailable"
          kicker={t('webAdmin.publicViewing.unavailableKicker')}
          icon={<CalendarClock size={16} strokeWidth={2} />}
          title={explain(viewing.error)}
          note={t('webAdmin.publicViewing.unavailableNote')}
          actions={null}
        />
      </main>
    );
  }

  const data: PublicViewing = viewing.data;
  const booking = data.booking;
  const showPicker = booking === null || moving;
  const canSubmit = chosen !== null && !busy && (moving || (name.trim() !== '' && /.+@.+\..+/.test(email.trim())));

  return (
    <main
      className="rh-v4-page rh-public-viewing"
      data-testid="public-viewing"
      style={officeAccentStyle(data.office.primaryColor) as React.CSSProperties}
    >
      <div className="rh-public-viewing__office" data-testid="public-viewing-office">
        {data.office.logoUrl !== null ? (
          <img src={data.office.logoUrl} alt="" className="rh-public-viewing__logo" />
        ) : null}
        <span>{data.office.name}</span>
      </div>
      <V4Header
        title={t('webAdmin.publicViewing.title')}
        lede={<span data-testid="public-viewing-property">{data.property.label}</span>}
        {...(data.property.address !== null && data.property.address !== data.property.label
          ? {
              note: (
                <span className="rh-public-viewing__address">
                  <MapPin size={14} strokeWidth={2} aria-hidden="true" /> {data.property.address}
                </span>
              ),
            }
          : {})}
      />

      {notice !== null ? (
        <p role="alert" className="rh-a2-error" data-testid="public-viewing-notice">
          {notice}
        </p>
      ) : null}
      {cancelled && booking === null ? (
        <p role="status" className="rh-a2-status" data-testid="public-viewing-cancelled">
          {t('webAdmin.publicViewing.cancelledNote')}
        </p>
      ) : null}

      {booking !== null && !moving ? (
        <V4NextStep
          testId="public-viewing-booked"
          kicker={t('webAdmin.publicViewing.bookedKicker')}
          icon={<CalendarCheck size={16} strokeWidth={2} />}
          title={formatViewingRange(booking.startsAt, booking.endsAt, locale)}
          note={t('webAdmin.publicViewing.bookedNote')}
          actions={
            <>
              <button
                type="button"
                className="rh-v4-btn rh-v4-btn--tinted"
                disabled={busy}
                onClick={() => {
                  setMoving(true);
                  setChosen(null);
                }}
                data-testid="public-viewing-move"
              >
                {t('webAdmin.publicViewing.move')}
              </button>
              <button
                type="button"
                className="rh-v4-btn rh-v4-btn--plain"
                disabled={busy}
                onClick={() => void cancel()}
                data-testid="public-viewing-cancel"
              >
                {t('webAdmin.publicViewing.cancel')}
              </button>
            </>
          }
        />
      ) : null}

      {showPicker ? (
        <>
          {/* Krok NAP6 A16: cíl odkazu „Vybrat termín" z chatu pod stránkou. */}
          <h2 className="rh-v4-h2" id="public-viewing-pick">
            {moving ? t('webAdmin.publicViewing.pickNewHeading') : t('webAdmin.publicViewing.pickHeading')}
          </h2>
          {data.slots.length === 0 ? (
            <p className="rh-a2-empty" data-testid="public-viewing-empty">
              {t('webAdmin.publicViewing.noSlots')}
            </p>
          ) : (
            groupSlotsByDay(data.slots).map((group) => (
              <V4Group key={group.day} label={formatViewingDay(group.items[0]?.startsAt ?? '', locale)} testId={`public-viewing-day-${group.day}`}>
                <div className="rh-public-viewing__day">{formatViewingDay(group.items[0]?.startsAt ?? '', locale)}</div>
                <div className="rh-public-viewing__slots" role="radiogroup" aria-label={t('webAdmin.publicViewing.pickHeading')}>
                  {group.items.map((slot) => (
                    <button
                      key={slot.id}
                      type="button"
                      role="radio"
                      aria-checked={chosen === slot.id}
                      className={`rh-public-viewing__slot${chosen === slot.id ? ' is-on' : ''}`}
                      onClick={() => setChosen(slot.id)}
                      data-testid={`public-viewing-slot-${slot.id}`}
                    >
                      {formatViewingTime(slot.startsAt, locale)}
                    </button>
                  ))}
                </div>
              </V4Group>
            ))
          )}

          {chosen !== null && !moving ? (
            <V4Group testId="public-viewing-form" label={t('webAdmin.publicViewing.formHeading')}>
              <div className="rh-public-viewing__form">
                <label className="rh-v4-field">
                  <span className="rh-v4-field__label">{t('webAdmin.publicViewing.name')}</span>
                  <input value={name} autoComplete="name" onChange={(e) => setName(e.target.value)} data-testid="public-viewing-name" />
                </label>
                <label className="rh-v4-field">
                  <span className="rh-v4-field__label">{t('webAdmin.publicViewing.email')}</span>
                  <input
                    type="email"
                    value={email}
                    autoComplete="email"
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="public-viewing-email"
                  />
                </label>
                <label className="rh-v4-field">
                  <span className="rh-v4-field__label">{t('webAdmin.publicViewing.phone')}</span>
                  <input
                    type="tel"
                    value={phone}
                    autoComplete="tel"
                    onChange={(e) => setPhone(e.target.value)}
                    data-testid="public-viewing-phone"
                  />
                </label>
                {phone.trim() !== '' ? (
                  <label className="rh-v4-field rh-v4-field--check">
                    <input
                      type="checkbox"
                      checked={smsConsent}
                      onChange={(e) => setSmsConsent(e.target.checked)}
                      data-testid="public-viewing-sms-consent"
                    />
                    <span className="rh-v4-field__label">{t('webAdmin.publicViewing.smsConsent')}</span>
                  </label>
                ) : null}
              </div>
            </V4Group>
          ) : null}

          {chosen !== null ? (
            <div className="rh-public-viewing__actions">
              <button
                type="button"
                className="rh-v4-btn rh-v4-btn--filled"
                disabled={!canSubmit}
                onClick={() => void submit()}
                data-testid="public-viewing-submit"
              >
                {moving ? t('webAdmin.publicViewing.confirmMove') : t('webAdmin.publicViewing.book')}
              </button>
              {moving ? (
                <button
                  type="button"
                  className="rh-v4-btn rh-v4-btn--plain"
                  onClick={() => {
                    setMoving(false);
                    setChosen(null);
                  }}
                >
                  {t('webAdmin.publicViewing.keep')}
                </button>
              ) : null}
            </div>
          ) : null}
          {moving && chosen === null ? (
            <V4Row
              title={t('webAdmin.publicViewing.keep')}
              onClick={() => setMoving(false)}
              testId="public-viewing-keep"
            />
          ) : null}
        </>
      ) : null}

      {/* Krok AI8: zájemce se zeptá i mimo pracovní dobu — odpověď jen z faktů
          nabídky, neznámá otázka jde makléři jako poptávka (server, ne UI). */}
      <PropertyChat
        token={token}
        {...(showPicker && data.slots.length > 0 ? { viewingPickerId: 'public-viewing-pick' } : {})}
      />

      <p className="rh-v4-head__lede rh-public-viewing__foot">
        {data.office.contactLine ?? data.office.name}
      </p>
    </main>
  );
}
